import * as stylex from '@stylexjs/stylex'
import { useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Icon, { type IconName } from "../../components/Icon";
import { formatMoney, type Overview } from "../../lib/ledger";


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#55735d",
  },
  s2: {
    position: "relative",
  },
  s4: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#5b9164",
  },
  s5: {
    marginTop: "4px",
    fontSize: "clamp(1.75rem,3.2vw,2.25rem)",
    fontWeight: 600,
  },
  s6: {
    marginTop: "8px",
    fontSize: "14px",
    color: "#707b72",
  },
  s7: {
    marginTop: "16px",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  s8: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
  },
  s9: {
    height: "16px",
    width: "16px",
  },
  s10: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e1e8df",
    backgroundColor: "#fff",
    padding: "20px",
  },
  s11: {
    fontSize: "15px",
    fontWeight: 600,
  },
  s12: {
    marginTop: "4px",
    fontSize: "12px",
    color: "#748077",
  },
  s13: {
    marginTop: "20px",
    display: "grid",
    gap: "20px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  s14: {
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "12px",
    fontWeight: 600,
    color: "#4b6351",
  },
  s15: {
    height: "160px",
    width: "100%",
    cursor: "grab",
    userSelect: "none",
  },
  s16: {
    overflow: "hidden",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e1e8df",
    backgroundColor: "#fff",
  },
  s17: {
    borderBottomWidth: 1,
    borderColor: "#edf0eb",
    paddingInline: "20px",
    paddingBlock: "12px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    color: "#889188",
  },
  s18: {
    paddingInline: "20px",
    paddingBlock: "48px",
    textAlign: "center",
    fontSize: "13px",
    color: "#748077",
  },
  s19: { borderTopWidth: 1, borderTopColor: "#edf0eb" },
  s20: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: "12px",
    paddingInline: "20px",
    paddingBlock: "12px",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f8fbf7",
    },
  },
  s21: {
    display: "flex",
    minWidth: "0px",
    flex: "1 1 0%",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    paddingBlock: "4px",
    textAlign: "left",
  },
  s22: {
    display: "flex",
    minWidth: "0px",
    alignItems: "center",
    gap: "12px",
  },
  s23: {
    display: "flex",
    height: "40px",
    width: "40px",
    flexShrink: "0",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.75rem",
    backgroundColor: "#f1f6ef",
    color: "#4a8258",
  },
  s24: {
    height: "20px",
    width: "20px",
  },
  s25: {
    minWidth: "0px",
  },
  s26: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontWeight: 600,
  },
  s27: {
    flexShrink: "0",
    fontSize: "14px",
    fontWeight: 600,
    color: "#314d39",
  },
  s28: {
    flexShrink: "0",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dce7da",
    paddingInline: "10px",
    paddingBlock: "8px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#3f7e4d",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#edf5eb",
    },
  },
  s29: {
    margin: "16px",
    height: "40px",
    width: "calc(100%-2rem)",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dce6da",
    fontSize: "12px",
    fontWeight: 600,
    color: "#3f7e4d",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f3f8f1",
    },
  },
  s30: {
    paddingInline: "20px",
    paddingBlock: "32px",
    textAlign: "center",
    fontSize: "13px",
    color: "#748077",
  },
  s31: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    paddingInline: "20px",
    paddingBlock: "16px",
  },
  s32: {
    position: "absolute",
    right: "0px",
    top: "0px",
  },
})
type Props = {
  data: Overview;
  personID: string;
  onBack: () => void;
  onEditPerson?: () => void;
  onOpenExpense?: (expenseID: string) => void;
  onAddExpense?: () => void;
  onRecordExchange?: (fromCurrency: string) => void;
};

function expenseIcon(description: string): { name: IconName; label: string } {
  const value = description.toLowerCase();
  if (/\b(rent|lease|mortgage|housing|apartment)\b/.test(value))
    return { name: "rent", label: "Housing" };
  if (
    /\b(food|grocery|groceries|dinner|lunch|breakfast|restaurant|cafe|café|coffee|pizza|burger|subway|fruit|fruits|bakery|dining|snack|meal)\b/.test(
      value,
    )
  )
    return { name: "food", label: "Food" };
  if (
    /\b(clothing|clothes|shirt|shirts|shoes|socks|pants|shein|zara|h&m|hm)\b/.test(
      value,
    )
  )
    return { name: "clothing", label: "Clothing" };
  if (
    /\b(klm|hotel|flight|plane|airbnb|reschedule|trip|bus|train|travel)\b/.test(
      value,
    )
  )
    return { name: "travel", label: "Travel" };
  if (/\b(parking|gas|fuel|car|vehicle|transport|taxi|uber)\b/.test(value))
    return { name: "transport", label: "Transport" };
  if (/\b(gift|present)\b/.test(value)) return { name: "gift", label: "Gift" };
  if (/\b(casino|movie|cinema|game|ticket|concert|entertainment)\b/.test(value))
    return { name: "entertainment", label: "Entertainment" };
  if (/\b(utility|utilities|electricity|water|internet|phone)\b/.test(value))
    return { name: "utilities", label: "Utilities" };
  if (
    /\b(amazon|shopping|shop|store|walmart|furniture|deodorant)\b/.test(value)
  )
    return { name: "shopping", label: "Shopping" };
  return { name: "receipt", label: "Expense" };
}

export default function PersonDetailPage({
  data,
  personID,
  onBack,
  onEditPerson,
  onOpenExpense,
  onAddExpense,
  onRecordExchange,
}: Props) {
  const [visibleCount, setVisibleCount] = useState(50);
  const [chartOffsets, setChartOffsets] = useState<Record<string, number>>({});
  const dragRef = useRef<{
    currency: string;
    startX: number;
    startOffset: number;
  } | null>(null);
  const person = data.people.find((item) => item.id === personID);
  if (!person) return null;
  const expenses = data.expenses
    .filter(
      (expense) =>
        (expense.paidById === personID ||
          expense.participantIds.includes(personID)) &&
        (expense.paidById === data.currentUser.id ||
          expense.participantIds.includes(data.currentUser.id)),
    )
    .sort(
      (a, b) =>
        new Date(b.occurredAt ?? b.createdAt).getTime() -
        new Date(a.occurredAt ?? a.createdAt).getTime(),
    );
  const exchanges = data.exchanges
    .filter((exchange) => exchange.personId === personID)
    .sort(
      (a, b) =>
        new Date(b.occurredAt ?? b.createdAt).getTime() -
        new Date(a.occurredAt ?? a.createdAt).getTime(),
    );
  const charts = Array.from(
    new Set([
      ...expenses.map((expense) => expense.currency),
      ...data.settlements.map((settlement) => settlement.currency),
    ]),
  ).map((currency) => {
    let running = 0;
    const events = [
      ...expenses
        .filter((expense) => expense.currency === currency)
        .map((expense) => ({
          date: expense.occurredAt ?? expense.createdAt,
          amount:
            expense.paidById === personID
              ? -expense.amountCents
              : expense.amountCents,
        })),
      ...data.settlements
        .filter(
          (settlement) =>
            settlement.currency === currency &&
            settlement.personId === personID,
        )
        .map((settlement) => ({
          date: settlement.paidAt ?? settlement.createdAt,
          amount:
            settlement.direction === "received"
              ? -settlement.amountCents
              : settlement.amountCents,
        })),
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return {
      currency,
      points: events.map((event) => {
        running += event.amount;
        return { date: event.date, value: running };
      }),
    };
  });
  return (
    <div {...stylex.props(styles.s0)}>
      <button
        {...stylex.props(styles.s1)}
        onClick={onBack}
        type="button"
      >
        ← All people
      </button>
      <header {...stylex.props(styles.s2)}>
        <button
          aria-label="Edit person details"
          {...stylex.props(styles.s8, styles.s32)}
          onClick={onEditPerson}
          type="button"
        >
          Edit
        </button>
        <p {...stylex.props(styles.s4)}>Shared ledger</p>
        <h1 {...stylex.props(styles.s5)}>
          You and {person.name}
        </h1>
        <p {...stylex.props(styles.s6)}>
          Every expense you share with this person.
        </p>
        <div {...stylex.props(styles.s7)}>
          <button
            {...stylex.props(styles.s8)}
            onClick={onAddExpense}
            type="button"
          >
            <Icon {...stylex.props(styles.s9)} name="plus" />
            Add expense
          </button>
        </div>
      </header>
      <section {...stylex.props(styles.s10)}>
        <h2 {...stylex.props(styles.s11)}>Balance over time</h2>
        <p {...stylex.props(styles.s12)}>
          Positive values show Patrick owing you. Hover the line to inspect any
          point.
        </p>
        <div {...stylex.props(styles.s13)}>
          {charts.map(({ currency, points }) => {
            const offset = chartOffsets[currency] ?? 0;
            const windowSize = 80;
            const start = Math.max(0, points.length - windowSize - offset);
            const visiblePoints = points.slice(start, start + windowSize);
            return (
              <div key={currency}>
                <div {...stylex.props(styles.s14)}>
                  <span>{currency}</span>
                  <span>
                    {formatMoney(points.at(-1)?.value ?? 0, currency)}
                  </span>
                </div>
                <div
                  {...stylex.props(styles.s15)}
                  onMouseDown={(event) => {
                    if (event.button === 1) {
                      event.preventDefault();
                      dragRef.current = {
                        currency,
                        startX: event.clientX,
                        startOffset: offset,
                      };
                    }
                  }}
                  onMouseMove={(event) => {
                    if (dragRef.current?.currency === currency) {
                      const delta = Math.round(
                        (event.clientX - dragRef.current.startX) / 8,
                      );
                      setChartOffsets((current) => ({
                        ...current,
                        [currency]: Math.max(
                          0,
                          Math.min(
                            points.length - windowSize,
                            dragRef.current!.startOffset + delta,
                          ),
                        ),
                      }));
                    }
                  }}
                  onMouseUp={() => {
                    dragRef.current = null;
                  }}
                  onMouseLeave={() => {
                    dragRef.current = null;
                  }}
                >
                  <ResponsiveContainer height="100%" width="100%">
                    <LineChart
                      data={visiblePoints}
                      margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid
                        stroke="#dce9da"
                        strokeDasharray="3 3"
                        vertical={false}
                      />
                      <XAxis
                        axisLine={false}
                        dataKey="date"
                        minTickGap={28}
                        tick={{ fill: "#7b887d", fontSize: 9 }}
                        tickFormatter={(value) =>
                          new Date(value).toLocaleDateString(undefined, {
                            month: "short",
                            year: "2-digit",
                          })
                        }
                        tickLine={false}
                      />
                      <YAxis
                        axisLine={false}
                        tick={{ fill: "#7b887d", fontSize: 9 }}
                        tickFormatter={(value) =>
                          formatMoney(Number(value), currency)
                        }
                        tickLine={false}
                        width={54}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#fffefa",
                          border: "1px solid #d9e6d7",
                          borderRadius: 10,
                          boxShadow: "0 6px 18px rgba(48,70,51,.12)",
                          fontSize: 12,
                        }}
                        formatter={(value) =>
                          formatMoney(Number(value), currency)
                        }
                        labelFormatter={(value) =>
                          new Date(String(value)).toLocaleDateString(
                            undefined,
                            { month: "short", day: "numeric", year: "numeric" },
                          )
                        }
                      />
                      <Line
                        activeDot={{ r: 4, strokeWidth: 2 }}
                        dataKey="value"
                        dot={false}
                        isAnimationActive={false}
                        stroke="#438b57"
                        strokeWidth={2}
                        type="monotone"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section {...stylex.props(styles.s16)}>
        <div {...stylex.props(styles.s17)}>
          Shared expenses ({expenses.length})
        </div>
        {expenses.length === 0 ? (
          <p {...stylex.props(styles.s18)}>
            No shared expenses yet.
          </p>
        ) : (
          <div {...stylex.props(styles.s19)}>
            {expenses.slice(0, visibleCount).map((expense) => {
              const category = expenseIcon(expense.description);
              return (
                <div
                  {...stylex.props(styles.s20)}
                  key={expense.id}
                >
                  <button
                    {...stylex.props(styles.s21)}
                    onClick={() => onOpenExpense?.(expense.id)}
                    type="button"
                  >
                    <div {...stylex.props(styles.s22)}>
                      <span
                        {...stylex.props(styles.s23)}
                        title={category.label}
                      >
                        <Icon {...stylex.props(styles.s24)} name={category.name} />
                      </span>
                      <div {...stylex.props(styles.s25)}>
                        <h2 {...stylex.props(styles.s26)}>
                          {expense.description}
                        </h2>
                        <p {...stylex.props(styles.s12)}>
                          {expense.paidById === data.currentUser.id
                            ? "You paid"
                            : `${person.name} paid`}{" "}
                          ·{" "}
                          {new Date(
                            expense.occurredAt ?? expense.createdAt,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <p {...stylex.props(styles.s27)}>
                      {formatMoney(expense.amountCents, expense.currency)}
                    </p>
                  </button>
                  {expense.currency !== data.currentUser.currency && (
                    <button
                      {...stylex.props(styles.s28)}
                      onClick={() => onRecordExchange?.(expense.currency)}
                      type="button"
                    >
                      Exchange
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {expenses.length > visibleCount && (
          <button
            {...stylex.props(styles.s29)}
            onClick={() => setVisibleCount((count) => count + 50)}
            type="button"
          >
            Load 50 more expenses
          </button>
        )}
      </section>
      <section {...stylex.props(styles.s16)}>
        <div {...stylex.props(styles.s17)}>
          Currency exchanges ({exchanges.length})
        </div>
        {exchanges.length === 0 ? (
          <p {...stylex.props(styles.s30)}>
            No currency exchanges yet.
          </p>
        ) : (
          <div {...stylex.props(styles.s19)}>
            {exchanges.map((exchange) => (
              <div
                {...stylex.props(styles.s31)}
                key={exchange.id}
              >
                <div {...stylex.props(styles.s22)}>
                  <span {...stylex.props(styles.s23)}>
                    <Icon {...stylex.props(styles.s24)} name="settle" />
                  </span>
                  <div {...stylex.props(styles.s25)}>
                    <h2 {...stylex.props(styles.s26)}>
                      {formatMoney(
                        exchange.fromAmountCents,
                        exchange.fromCurrency,
                      )}{" "}
                      →{" "}
                      {formatMoney(exchange.toAmountCents, exchange.toCurrency)}
                    </h2>
                    <p {...stylex.props(styles.s12)}>
                      1 {exchange.fromCurrency} ={" "}
                      {(exchange.rateBps / 10000).toFixed(2)}{" "}
                      {exchange.toCurrency} ·{" "}
                      {new Date(
                        exchange.occurredAt ?? exchange.createdAt,
                      ).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
